import { useState, useRef, useEffect } from "react";
import { UserPlus, X, Check, Search } from "lucide-react";

const DEFAULT_COLOR = "#0ea5e9";

function initials(name) {
  return (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export function AssigneePicker({ value, onChange, users = [], multi = true, testid, placeholder = "Assign", floatRight = false, size = "h-7 w-7" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selectedIds = multi ? (value || []) : (value ? [value] : []);
  const byId = {};
  users.forEach((u) => { byId[u.id] = u; });

  const toggle = (id) => {
    if (multi) {
      onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    } else {
      onChange(selectedIds.includes(id) ? null : id);
      setOpen(false);
    }
  };
  const remove = (id) => (multi ? onChange(selectedIds.filter((x) => x !== id)) : onChange(null));

  const query = q.trim().toLowerCase();
  const list = users.filter((u) => !query || (u.name || "").toLowerCase().includes(query));
  const textSize = size.includes("6") ? "text-[9px]" : "text-[10px]";

  return (
    <div ref={ref} className="relative inline-flex" data-testid={testid}>
      <div className={`flex flex-wrap items-center gap-1 ${floatRight ? "justify-end" : ""}`}>
        {selectedIds.map((id) => {
          const u = byId[id];
          return (
            <span
              key={id}
              title={u?.name || "Unknown"}
              data-testid={`${testid}-chip-${id}`}
              onClick={() => remove(id)}
              className={`group relative flex ${size} shrink-0 cursor-pointer items-center justify-center rounded-full ${textSize} font-bold text-white ring-2 ring-white`}
              style={{ backgroundColor: u?.color || DEFAULT_COLOR }}
            >
              {initials(u?.name)}
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <X className="h-3 w-3" />
              </span>
            </span>
          );
        })}
        <button
          type="button"
          data-testid={`${testid}-add`}
          onClick={() => setOpen((o) => !o)}
          title={placeholder}
          className={`flex ${size} shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-sky-400 hover:text-sky-500`}
        >
          <UserPlus className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && (
        <div className={`absolute top-full z-50 mt-1 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg ${floatRight ? "right-0" : "left-0"}`}>
          <div className="flex items-center gap-1.5 border-b border-slate-100 px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              data-testid={`${testid}-search`}
              className="w-full border-0 bg-transparent py-0.5 text-sm outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {list.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs text-slate-400">No people</p>
            ) : (
              list.map((u) => {
                const sel = selectedIds.includes(u.id);
                return (
                  <button
                    type="button"
                    key={u.id}
                    data-testid={`${testid}-option-${u.id}`}
                    onClick={() => toggle(u.id)}
                    className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-slate-50"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: u.color || DEFAULT_COLOR }}>
                      {initials(u.name)}
                    </span>
                    <span className="flex-1 truncate text-slate-700">{u.name}</span>
                    {sel && <Check className="h-4 w-4 text-sky-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
